/**
 * App Tour catalog — read-side helpers over the generated lesson list.
 * Data comes from docs/tour/lessons.md via scripts/gen-tour-catalog.mjs.
 */
import { resolveProfile, type ProfileUser } from '@/lib/profileIdentity';
import { TOUR_FIRST_DAY, TOUR_FIRST_DAY_WEB, TOUR_LESSONS, TOUR_MODULES } from './tourCatalog.generated';
import type { TourLesson, TourModule } from './tourTypes';

export type { TourLesson, TourModule } from './tourTypes';
export type TourPlatform = 'web' | 'android';
export type TourScope = 'first-day' | 'all';
/**
 * Who is looking.
 * - 'admin' (P18): every lesson, including the admin-only ones (phone setup,
 *   calling/recording and location permissions).
 * - 'manager' (P01): the manager-only lessons, not the admin-only ones.
 * - 'coordinator' (P02): like the manager (Manak, 2026-10-04) — the manager-only
 *   lessons are her manager's screens and read "For managers" for her.
 * - 'team' (everyone else): neither.
 */
export type TourAudience = 'admin' | 'manager' | 'coordinator' | 'team';

const BY_ID = new Map(TOUR_LESSONS.map((lesson) => [lesson.id, lesson]));
const FIRST_DAY = new Set([...TOUR_FIRST_DAY, ...TOUR_FIRST_DAY_WEB]);

export function tourAudience(user: ProfileUser | null | undefined): TourAudience {
  const profile = resolveProfile(user);
  if (profile === 'P18') return 'admin';
  if (profile === 'P01') return 'manager';
  return profile === 'P02' ? 'coordinator' : 'team';
}

const MANAGER_ONLY = /^manager only/i;

/** A coordinator reads "Manager only (…)" as "For managers (…)": her manager's screens, not hers. */
function forAudience(text: string, audience: TourAudience): string {
  return audience === 'coordinator' ? text.replace(MANAGER_ONLY, 'For managers') : text;
}

export function moduleTitleFor(module: TourModule | undefined, audience: TourAudience): string {
  return module ? forAudience(module.title, audience) : '';
}

export function lessonWhoFor(lesson: TourLesson, audience: TourAudience): string {
  return forAudience(lesson.who, audience);
}

export function findLesson(id: string | undefined): TourLesson | undefined {
  return id ? BY_ID.get(id) : undefined;
}

export function findModule(id: string | undefined): TourModule | undefined {
  return TOUR_MODULES.find((module) => module.id === id);
}

/** Role filter: admin-only → admin; manager-only → manager, coordinator and admin; the rest → everyone. */
export function canSeeLesson(lesson: TourLesson, audience: TourAudience): boolean {
  if (lesson.adminOnly) return audience === 'admin';
  if (lesson.managerOnly) return audience !== 'team';
  return true;
}

/** Web lists Web + Both lessons; the Android switch lists Android + Both. */
export function onPlatform(lesson: TourLesson, platform: TourPlatform): boolean {
  return lesson.platform === 'Both' || lesson.platform === (platform === 'web' ? 'Web' : 'Android');
}

/**
 * "Allowed": the lesson list says the lesson may run as a walk. Android lessons are
 * watch-only on the web: the guided walk needs the real web page.
 * Whether a walk is actually wired is `hasWalk` in tourWalks.ts.
 */
export function canShowMe(lesson: TourLesson, platform: TourPlatform): boolean {
  return platform === 'web' && lesson.showMe.allowed;
}

export function lessonMinutes(lesson: TourLesson, platform: TourPlatform): number {
  return lesson.minutes[platform] ?? lesson.minutes.web ?? lesson.minutes.android ?? 0;
}

export function matchesSearch(lesson: TourLesson, query: string): boolean {
  const q = query.trim().toLowerCase();
  if (!q) return true;
  const module = findModule(lesson.moduleId);
  return `${lesson.id} ${lesson.title} ${module?.title ?? ''}`.toLowerCase().includes(q);
}

export interface TourFilter {
  audience: TourAudience;
  platform: TourPlatform;
  scope: TourScope;
}

/** Every lesson this person can open on this platform, in catalog order. */
export function visibleLessons({ audience, platform, scope }: TourFilter): TourLesson[] {
  return TOUR_LESSONS.filter((lesson) =>
    canSeeLesson(lesson, audience) && onPlatform(lesson, platform) && (scope === 'all' || FIRST_DAY.has(lesson.id)));
}

export function visibleModules(lessons: TourLesson[]): TourModule[] {
  const ids = new Set(lessons.map((lesson) => lesson.moduleId));
  return TOUR_MODULES.filter((module) => ids.has(module.id));
}
